import clsx from "clsx";
import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch } from "src/store";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";

import { useCurrentWorkspace, useCurrentWorkspaceFeatures } from "@dashboard/workspaces/api/helpers";
import { fetchGroups, generateLicenses } from "@devices/store";
import { Box, Theme, Typography, TypographyProps } from "@mui/material";
import { PermissionService } from "@services/permissionService/permissionService";
import { PrimaryButton } from "@shared/CustomButton";
import { NumberField } from "@shared/form/NumberField";
import { LoadingBox } from "@shared/Loading";
import { TextLink } from "@shared/TextLink";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";
import { PaneElement } from "@sidePanel/shared/PaneElement";
import { useWorkspaceFeature } from "@workspaces/api/helpers";

type FormValues = { grantCount: number };

type GenerateLicensesSectionProps = {
    groupId: number;
    groupName: string;
    groupStatistics: API.GroupStatistics | null;
};

const subscriptionLink = "https://emteria.com/p/subscription";

const useStyles = makeStyles()((theme: Theme) => ({
    content: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
        marginTop: theme.spacing(3),
    },
    bars: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
    },
    bar: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
    // The empty part of the track is what "can be generated" means, so the track carries that
    // colour and the border keeps the bar's extent visible even when it is nearly empty.
    barTrack: {
        display: "flex",
        boxSizing: "border-box",
        width: "100%",
        height: 12,
        backgroundColor: theme.palette.emteria.background.paperDeep,
        border: `1px solid ${theme.palette.emteria.border.primary}`,
        borderRadius: 50,
        overflow: "hidden",
    },
    barSegment: {
        height: "100%",
        transition: "width 0.3s ease",
    },
    legend: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(2),
    },
    legendItem: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    legendDot: {
        width: 10,
        height: 10,
        flexShrink: 0,
        borderRadius: "50%",
    },
    // emteria.primary and emteria.success both invert between light and dark theme, so the
    // filled segments stay legible on either background.
    filledSlots: { backgroundColor: theme.palette.emteria.primary },
    unusedSlots: { backgroundColor: theme.palette.emteria.success },
    // The empty fill is a near-invisible grey in both themes, so this swatch is carried by its
    // ring rather than its fill.
    emptySlots: {
        backgroundColor: theme.palette.emteria.background.paperDeep,
        border: `1px solid ${theme.palette.emteria.text.secondary}`,
    },
    limitReachedText: { marginTop: theme.spacing(0.5), display: "block" },
    externalLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
    },
    generateFormRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        flexWrap: "wrap",
    },
    slotField: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
    },
    slotInput: {
        // Wide enough for the two-digit counts users realistically enter
        width: 72,
        position: "relative",
        // A validation message wrapped into a 72px column becomes one word per line, so let it
        // hang below the row at its natural width instead. Doubled selector to win over the
        // shared field styles.
        "&& .MuiFormHelperText-root": {
            position: "absolute",
            top: "100%",
            left: 0,
            width: "max-content",
            marginLeft: 0,
            whiteSpace: "nowrap",
        },
    },
    limitReachedAlert: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(2),
        border: `1px solid ${theme.palette.emteria.alerts.warning.border}`,
        backgroundColor: theme.palette.emteria.alerts.warning.bg,
        padding: theme.spacing(2),
    },
    limitReachedIcon: {
        color: theme.palette.emteria.warning,
        marginTop: 4,
        flexShrink: 0,
        fontSize: theme.typography.body1.fontSize,
    },
    iconSmall: {
        fontSize: theme.typography.caption.fontSize,
    },
    iconMedium: {
        fontSize: theme.typography.body2.fontSize,
    },
    requestLimitLink: {
        display: "flex",
        justifyContent: "flex-end",
    },
    featuresLoadingBox: {
        minHeight: 160,
        marginTop: 0,
        alignItems: "center",
        justifyContent: "center",
    },
}));

// A count of 0 must not paint anything, while a count that rounds below a pixel still has to be
// visible — hence the floor of 2px rather than a plain percentage.
const segmentStyle = (count: number, total: number): React.CSSProperties => ({
    width: total > 0 ? `${(count / total) * 100}%` : 0,
    minWidth: count > 0 ? 2 : 0,
});

const SubscriptionLink = ({
    children,
    tooltip,
    className,
    iconClassName,
    variant,
}: {
    children?: React.ReactNode;
    tooltip: string;
    className: string;
    iconClassName: string;
    variant?: TypographyProps["variant"];
}) => (
    <TextLink href={subscriptionLink} target="_blank" tooltip={tooltip} className={className} variant={variant}>
        {children}
        <i className={`fa-solid fa-arrow-up-right-from-square ${iconClassName}`} />
    </TextLink>
);

export const GenerateLicensesSection = ({ groupId, groupName, groupStatistics }: GenerateLicensesSectionProps) => {
    const [isGenerating, setIsGenerating] = React.useState(false);
    const { classes } = useStyles();
    const { t } = useTranslation(["common", "sidepanels"]);
    const dispatch = useDispatch();

    const { data: workspace } = useCurrentWorkspace();
    const licenseLimit = useWorkspaceFeature("LicenseLimit");
    const canCreateLicense = PermissionService().workspaceAbility(workspace).can("create", "License");
    const { data: features = [], isLoading: featuresLoading } = useCurrentWorkspaceFeatures();

    const licenseFeature = features.find((f) => f.feature === "LicenseLimit");
    const slotStatus = groupStatistics?.slotStatus;
    // used/unused are this group's own slots, while available counts what the whole workspace may
    // still generate — the two are never parts of the same total.
    const usedCount = slotStatus?.used ?? 0;
    const unusedCount = slotStatus?.unused ?? 0;
    const groupSlots = usedCount + unusedCount;
    const freeSlotsFromGroupStats = slotStatus?.available;
    const freeLicenseSlots =
        freeSlotsFromGroupStats !== undefined ? freeSlotsFromGroupStats : Math.max(0, licenseLimit - (licenseFeature?.used ?? 0));
    // Derived from the limit rather than read from the feature's own "used" count, so the bar and
    // its legend always add up to the limit.
    const generatedSlots = Math.max(0, licenseLimit - freeLicenseSlots);
    // An unlimited workspace has no denominator to draw against.
    const showWorkspaceBar = Number.isFinite(licenseLimit);

    const handleGenerate = async (grantCount: number) => {
        try {
            setIsGenerating(true);
            await dispatch(generateLicenses({ groupId, grantCount })).unwrap();
            if (workspace?.id) {
                await dispatch(fetchGroups(workspace.id)).unwrap();
            }
            showSuccessToast(t("sidepanels:groupPanel.toast.licenseGenerated", { name: groupName }));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
            throw err;
        } finally {
            setIsGenerating(false);
        }
    };

    const validate = useValidationSchema(
        z.object({
            grantCount: z.coerce
                .number()
                .int(t("sidepanels:groupPanel.input.grantCount.validation.integer"))
                .min(1, t("sidepanels:groupPanel.input.grantCount.validation.greaterThan"))
                .max(freeLicenseSlots, t("sidepanels:groupPanel.input.grantCount.validation.max", { freeLicenseSlots })),
        }),
    );

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        await handleGenerate(values.grantCount);
        form.restart({ grantCount: 1 });
    };

    return (
        <>
            <PaneElement.Header>{t("sidepanels:groupPanel.enrollmentTab.deviceSlotUsage")}</PaneElement.Header>
            {featuresLoading ? (
                <LoadingBox className={classes.featuresLoadingBox} />
            ) : (
                <>
                    <PaneElement.Text>
                        {t("sidepanels:groupPanel.enrollmentTab.deviceSlotUsageDescription", { slots: licenseLimit })}
                    </PaneElement.Text>
                    <Box className={classes.content}>
                        {freeLicenseSlots < 1 ? (
                            <Box className={classes.limitReachedAlert}>
                                <i className={`fa-solid fa-circle-info ${classes.limitReachedIcon}`} />
                                <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.primary"
                                    className={classes.limitReachedText}
                                >
                                    <Trans
                                        ns="sidepanels"
                                        i18nKey="groupPanel.enrollmentTab.limitReachedMessage"
                                        values={{ total: licenseLimit }}
                                        components={{
                                            // Deliberately not named "link": html-parse-stringify treats
                                            // <link> as a void element, so Trans would drop the label out
                                            // of the anchor and leave it unclickable.
                                            requestLink: (
                                                <SubscriptionLink
                                                    tooltip={t("sidepanels:groupPanel.tooltip.requestMoreSlots")}
                                                    className={classes.externalLink}
                                                    iconClassName={classes.iconSmall}
                                                />
                                            ),
                                        }}
                                    />
                                </Typography>
                            </Box>
                        ) : (
                            <Form<FormValues>
                                onSubmit={onSubmit}
                                validate={validate}
                                initialValues={{ grantCount: 1 }}
                                render={({ handleSubmit }) => (
                                    <form onSubmit={handleSubmit}>
                                        <Box className={classes.generateFormRow}>
                                            <Box className={classes.slotField}>
                                                <Typography color="text.primary">
                                                    {t("sidepanels:groupPanel.enrollmentTab.generateSlotsLabel")}
                                                </Typography>
                                                <NumberField
                                                    name="grantCount"
                                                    className={classes.slotInput}
                                                    slotProps={{
                                                        htmlInput: {
                                                            min: 1,
                                                            max: Math.max(1, freeLicenseSlots),
                                                            step: 1,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                            <Tooltip
                                                hide={canCreateLicense}
                                                title={t("sidepanels:groupPanel.tooltip.generateNewLicenses")}
                                            >
                                                <PrimaryButton
                                                    type="submit"
                                                    loading={isGenerating}
                                                    disabled={!canCreateLicense || isGenerating || freeLicenseSlots < 1}
                                                >
                                                    {t("common:button.generate")}
                                                </PrimaryButton>
                                            </Tooltip>
                                        </Box>
                                    </form>
                                )}
                            />
                        )}
                        <Box className={classes.bars}>
                            <Box className={classes.bar}>
                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                    {t("sidepanels:groupPanel.enrollmentTab.slotsInGroup")}
                                </Typography>
                                <Box className={classes.barTrack}>
                                    <Box
                                        className={clsx(classes.barSegment, classes.filledSlots)}
                                        style={segmentStyle(usedCount, groupSlots)}
                                    />
                                    <Box
                                        className={clsx(classes.barSegment, classes.unusedSlots)}
                                        style={segmentStyle(unusedCount, groupSlots)}
                                    />
                                </Box>
                                <Box className={classes.legend}>
                                    <Box className={classes.legendItem}>
                                        <Box className={clsx(classes.legendDot, classes.filledSlots)} />
                                        <Typography variant="body2" fontWeight={500} color="text.primary">
                                            {t("sidepanels:groupPanel.enrollmentTab.usedByDevices", { count: usedCount })}
                                        </Typography>
                                    </Box>
                                    <Box className={classes.legendItem}>
                                        <Box className={clsx(classes.legendDot, classes.unusedSlots)} />
                                        <Typography variant="body2" fontWeight={500} color="text.primary">
                                            {t("sidepanels:groupPanel.enrollmentTab.currentlyUnused", { count: unusedCount })}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {showWorkspaceBar ? (
                                <Box className={classes.bar}>
                                    <Typography variant="body2" fontWeight={600} color="text.primary">
                                        {t("sidepanels:groupPanel.enrollmentTab.slotsInWorkspace")}
                                    </Typography>
                                    <Box className={classes.barTrack}>
                                        <Box
                                            className={clsx(classes.barSegment, classes.filledSlots)}
                                            style={segmentStyle(generatedSlots, licenseLimit)}
                                        />
                                    </Box>
                                    <Box className={classes.legend}>
                                        <Box className={classes.legendItem}>
                                            <Box className={clsx(classes.legendDot, classes.filledSlots)} />
                                            <Typography variant="body2" fontWeight={500} color="text.primary">
                                                {t("sidepanels:groupPanel.enrollmentTab.alreadyGenerated", {
                                                    count: generatedSlots,
                                                })}
                                            </Typography>
                                        </Box>
                                        <Box className={classes.legendItem}>
                                            <Box className={clsx(classes.legendDot, classes.emptySlots)} />
                                            <Typography variant="body2" fontWeight={500} color="text.primary">
                                                {t("sidepanels:groupPanel.enrollmentTab.canBeGenerated", {
                                                    count: freeLicenseSlots,
                                                })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            ) : null}
                        </Box>
                        {freeLicenseSlots < 1 ? (
                            <Box className={classes.requestLimitLink}>
                                <SubscriptionLink
                                    variant="body2"
                                    tooltip={t("sidepanels:groupPanel.tooltip.requestMoreSlots")}
                                    className={classes.externalLink}
                                    iconClassName={classes.iconMedium}
                                >
                                    {t("sidepanels:groupPanel.enrollmentTab.requestLimitIncrease")}
                                </SubscriptionLink>
                            </Box>
                        ) : null}
                    </Box>
                </>
            )}
        </>
    );
};
